import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { createAssessment } from 'stores/AssessmentStoreAdapter';
import { MasterTypeResult, getAssessmentType } from 'stores/MasterTypeStoreAdapter';
import { UserResult } from 'stores/UserStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type AssessmentCreateFormHandle = {
  submit: () => void;
};

type AssessmentCreateFormProps = {
  users: UserResult[];
  onCreate: () => void;
  onCancel: () => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const AssessmentCreateForm = React.forwardRef<
  AssessmentCreateFormHandle,
  AssessmentCreateFormProps
>(({ users, onCreate, onCancel, setProgress, hideHeader = false }, ref) => {
  const [assessmentTypeCode, setAssessmentTypeCode] = useState('');
  const [assessmentName, setAssessmentName] = useState('');
  const [managementNumber1, setManagementNumber1] = useState('');
  const [managementNumber2, setManagementNumber2] = useState('');
  const [managementNumber3, setManagementNumber3] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [managerId, setManagerId] = useState<string | null>(null);
  const [information1, setInformation1] = useState('');
  const [information2, setInformation2] = useState('');
  const [information3, setInformation3] = useState('');
  const [note, setNote] = useState('');

  const [assessmentTypes, setAssessmentTypes] = useState<MasterTypeResult[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    getAssessmentType()
      .then(setAssessmentTypes)
      .catch(() => errorDefault('資産区分の取得に失敗しました'));
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!assessmentTypeCode) newErrors.assessmentTypeCode = MSG_REQUIRED('資産区分');
    if (!assessmentName) newErrors.assessmentName = MSG_REQUIRED('資産名');
    else if (assessmentName.length > 200) newErrors.assessmentName = MSG_MAX_LENGTH('資産名', 200);

    if (managementNumber1 && managementNumber1.length > 50)
      newErrors.managementNumber1 = MSG_MAX_LENGTH('管理番号1', 50);
    if (managementNumber2 && managementNumber2.length > 50)
      newErrors.managementNumber2 = MSG_MAX_LENGTH('管理番号2', 50);
    if (managementNumber3 && managementNumber3.length > 50)
      newErrors.managementNumber3 = MSG_MAX_LENGTH('管理番号3', 50);

    if (!userId) newErrors.userId = MSG_REQUIRED('利用者');
    if (!managerId) newErrors.managerId = MSG_REQUIRED('管理者');

    if (information1 && information1.length > 100)
      newErrors.information1 = MSG_MAX_LENGTH('情報1', 100);
    if (information2 && information2.length > 100)
      newErrors.information2 = MSG_MAX_LENGTH('情報2', 100);
    if (information3 && information3.length > 100)
      newErrors.information3 = MSG_MAX_LENGTH('情報3', 100);

    if (note && note.length > 10000) newErrors.note = MSG_MAX_LENGTH('備考', 10000);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    if (!validate()) return;

    setProgress(true);
    createAssessment({
      assessmentTypeCode,
      assessmentName,
      ...(managementNumber1 && { managementNumber1 }),
      ...(managementNumber2 && { managementNumber2 }),
      ...(managementNumber3 && { managementNumber3 }),
      userId: userId!,
      managerId: managerId!,
      ...(information1 && { information1 }),
      ...(information2 && { information2 }),
      ...(information3 && { information3 }),
      ...(note && { note }),
    })
      .then(() => {
        successDefault('資産を登録しました');
        onCreate();
      })
      .catch((e: Error) => {
        errorDefault('資産の登録に失敗しました', e.message);
      })
      .finally(() => setProgress(false));
  };

  useImperativeHandle(ref, () => ({ submit: handleCreate }));

  return (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!hideHeader && (
        <Box sx={STICKY_HEADER_SX}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton size="small" onClick={onCancel}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography variant="h6" fontWeight={700}>
                資産登録
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
            新しい資産を登録します
          </Typography>
        </Box>
      )}

      <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
        {/* 基本情報 */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
            基本情報
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <FormControl fullWidth size="small" required error={!!errors.assessmentTypeCode}>
                <InputLabel>資産区分</InputLabel>
                <Select
                  value={assessmentTypeCode}
                  onChange={(e) => setAssessmentTypeCode(e.target.value)}
                  label="資産区分"
                  sx={{ borderRadius: 2 }}
                >
                  {assessmentTypes.map((t) => (
                    <MenuItem key={t.typeCode} value={t.typeCode}>
                      {t.typeLabel}
                    </MenuItem>
                  ))}
                </Select>
                {errors.assessmentTypeCode && (
                  <FormHelperText>{errors.assessmentTypeCode}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField
                label="資産名"
                value={assessmentName}
                onChange={(e) => setAssessmentName(e.target.value)}
                fullWidth
                size="small"
                required
                error={!!errors.assessmentName}
                helperText={errors.assessmentName}
                slotProps={{ htmlInput: { maxLength: 200 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* 管理番号 */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
            管理番号
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="管理番号1"
                value={managementNumber1}
                onChange={(e) => setManagementNumber1(e.target.value)}
                fullWidth
                size="small"
                error={!!errors.managementNumber1}
                helperText={errors.managementNumber1}
                slotProps={{ htmlInput: { maxLength: 50 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="管理番号2"
                value={managementNumber2}
                onChange={(e) => setManagementNumber2(e.target.value)}
                fullWidth
                size="small"
                error={!!errors.managementNumber2}
                helperText={errors.managementNumber2}
                slotProps={{ htmlInput: { maxLength: 50 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="管理番号3"
                value={managementNumber3}
                onChange={(e) => setManagementNumber3(e.target.value)}
                fullWidth
                size="small"
                error={!!errors.managementNumber3}
                helperText={errors.managementNumber3}
                slotProps={{ htmlInput: { maxLength: 50 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* 利用者・管理者 */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
            利用者・管理者
          </Typography>
          <Grid container spacing={2}>
            <Grid size={6}>
              <UserSelect
                mode="single"
                users={users}
                selectedUserId={userId}
                onChange={setUserId}
                label="利用者"
                required
                error={!!errors.userId}
                helperText={errors.userId}
              />
            </Grid>
            <Grid size={6}>
              <UserSelect
                mode="single"
                users={users}
                selectedUserId={managerId}
                onChange={setManagerId}
                label="管理者"
                required
                error={!!errors.managerId}
                helperText={errors.managerId}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* 情報 */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
            情報
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                label="情報1"
                value={information1}
                onChange={(e) => setInformation1(e.target.value)}
                fullWidth
                size="small"
                error={!!errors.information1}
                helperText={errors.information1}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="情報2"
                value={information2}
                onChange={(e) => setInformation2(e.target.value)}
                fullWidth
                size="small"
                error={!!errors.information2}
                helperText={errors.information2}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="情報3"
                value={information3}
                onChange={(e) => setInformation3(e.target.value)}
                fullWidth
                size="small"
                error={!!errors.information3}
                helperText={errors.information3}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* 備考 */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
            備考
          </Typography>
          <TextArea
            label="備考"
            value={note}
            onChange={setNote}
            maxLength={10000}
            rows={6}
            fullWidth
            errorMessage={errors.note}
          />
        </Box>
      </Stack>
    </Stack>
  );
});

AssessmentCreateForm.displayName = 'AssessmentCreateForm';

export default AssessmentCreateForm;
